<template>
  <CModal :show="show" :closeOnBackdrop="false" :centered="true" size="lg">
    <template #header>
      <h5 class="modal-title">{{ title }}</h5>
    </template>
    <div v-if="!loading">
      <form @submit.prevent="onSubmit">
        <div class="form-group position-relative z-3">
          <label>{{ $t("options.roletype.producer") }}</label>
          <input
            type="text"
            class="form-control"
            v-model="producerSearch"
            @input="onProducerSearch"
            @focus="showProducerDropdown = true"
            @blur="hideProducerDropdown"
            placeholder=""
            autocomplete="off"
          />
          <div
            v-if="showProducerDropdown && filteredProducers.length > 0"
            class="list-group position-absolute w-100 bg-white"
            style="z-index: 1000; max-height: 200px; overflow-y: auto"
          >
            <button
              type="button"
              v-for="producer in filteredProducers"
              :key="producer.id"
              class="list-group-item list-group-item-action"
              @mousedown.prevent="selectProducer(producer)"
            >
              {{ producer.role.name }}
            </button>
          </div>
        </div>

        <div class="form-group">
          <label>{{ $t("models.mpc.order_offset") }}</label>
          <CInput v-model.number="form.order_offset" type="number" min="0" />
        </div>

        <div class="form-group">
          <label>{{ $t("models.mpc.delivery_offset") }}</label>
          <CInput v-model.number="form.delivery_offset" type="number" min="0" />
        </div>

        <div class="form-group">
          <label>{{ $t("models.mpc.market_wh") }}</label>
          <select
            class="form-control"
            v-model="form.market_wh"
            @change="onWhChange(form.market_wh)"
          >
            <option value="0">
              {{ $t("family.settings.addresses.modal.country_select") }}
            </option>
            <option v-for="w in wh" :key="w.id" :value="w.id">
              {{ w.address.to }}
            </option>
          </select>
        </div>

        <div class="form-group">
          <label>{{ $t("models.mpc.delivery_type") }}</label>
          <select
            class="form-control"
            v-model="form.delivery_type"
            @change="onDeliveryTypeChange(form.delivery_type)"
          >
            <option value="0">
              {{ $t("family.settings.addresses.modal.country_select") }}
            </option>
            <option v-for="dt in DeliveryType" :key="dt.id" :value="dt.id">
              {{ dt.name }}
            </option>
          </select>
        </div>

        <div class="form-group">
          <label>{{ $t("models.mpc.delivery_method") }}</label>
          <select
            class="form-control"
            v-model="form.delivery_method"
            @change="onDeliveryModeChange(form.delivery_method)"
          >
            <option value="0">
              {{ $t("family.settings.addresses.modal.country_select") }}
            </option>
            <option v-for="dm in DeliveryMode" :key="dm.id" :value="dm.id">
              {{ dm.name }}
            </option>
          </select>
        </div>
      </form>

      <CAlert v-if="validationError" color="danger" :show.sync="showAlert">
        {{ validationError }}
      </CAlert>
    </div>
    <div
      v-else
      class="d-flex justify-content-center align-items-center"
      style="height: 200px"
    >
      <CSpinner color="primary" />
    </div>

    <template #footer>
      <CButton color="primary" variant="outline" @click.prevent="closeModal">
        {{ $t("common.editor.cancel") }}
      </CButton>
      <vue-ladda
        :loading="loading"
        data-style="zoom-in"
        button-class="btn btn-primary px-4"
        @click.prevent="onSubmit"
      >
        {{ $t("common.editor.confirm") }}
      </vue-ladda>
    </template>
  </CModal>
</template>

<script>
import vSelect from "vue-select";
import { apiPatch, apiPost, apiGet } from "../../../helpers/apiHelper";

export default {
  name: "ProducerModal",
  components: {
    vSelect,
  },
  props: {
    show: {
      type: Boolean,
      default: false,
    },
    title: {
      type: String,
      default: "",
    },
    editMode: {
      type: Boolean,
      default: false,
    },
    editId: {
      type: [Number, String],
      default: null,
    },
    values: {
      type: Object,
      default: () => ({}),
    },
    wh: {
      type: Array,
      default: () => [],
    },
    DeliveryMode: {
      type: Array,
      default: () => [],
    },
    DeliveryType: {
      type: Array,
      default: () => [],
    },
  },

  data() {
    return {
      loading: false,
      validationError: null,
      showAlert: false,
      producerSearch: "",
      showProducerDropdown: false,
      form: {
        producer: { role: { name: null, id: null } },
        market_wh: 0,
        delivery_type: 0,
        delivery_method: 0,
        order_offset: 0,
        delivery_offset: 0,
      },
      producerData: [],
    };
  },

  computed: {
    filteredProducers() {
      if (!this.producerSearch || this.producerSearch.length < 2) {
        return [];
      }
      const search = this.producerSearch.toLowerCase();
      return this.producerData.filter((p) =>
        p.role.name.toLowerCase().includes(search)
      );
    },
  },

  watch: {
    show: {
      handler(isShown) {
        if (isShown) {
          if (this.values && Object.keys(this.values).length > 0) {
            this.populateForm(this.values);
          } else {
            this.resetForm();
          }
        }
      },
      immediate: false,
    },
    values: {
      handler(newValues) {
        if (newValues && Object.keys(newValues).length > 0) {
          this.populateForm(newValues);
        }
      },
      immediate: true,
    },
  },

  methods: {
    onProducerSearch() {
      this.showProducerDropdown = true;
    },

    selectProducer(producer) {
      this.form.producer = {
        role: {
          name: producer.id,
          id: producer.role.name,
        },
      };
      this.producerSearch = producer.role.name;
      this.showProducerDropdown = false;
    },

    hideProducerDropdown() {
      setTimeout(() => {
        this.showProducerDropdown = false;
      }, 200);
    },

    onWhChange(marketWh) {
      this.form.market_wh = marketWh;
    },

    onDeliveryModeChange(deliveryMethod) {
      this.form.delivery_method = deliveryMethod;
    },

    onDeliveryTypeChange(deliveryType) {
      this.form.delivery_type = deliveryType;
    },

    populateForm(data) {
      this.form.producer = {
        role: {
          id: data.producer?.role?.name || null,
          name: data.producer?.id || null,
        },
      };
      this.producerSearch = data.producer?.role?.name || "";
      this.form.market_wh = data.market_wh || 0;
      this.form.delivery_type = data.delivery_type || 0;
      this.form.delivery_method = data.delivery_method || 0;
      this.form.order_offset = data.order_offset || 0;
      this.form.delivery_offset = data.delivery_offset || 0;
      this.loading = false;
    },

    async fetchProducers() {
      const response = await apiGet(
        "role_marketproducer",
        [this.$store.state.role.id],
        null
      );
      if (response && response.status === 200) {
        this.producerData = response.data.payload.Items;
      } else {
        this.$store.dispatch("toaster/add", {
          title: this.$t("role.finance.invoicesSettings.attention"),
          text: "Error loading producer data",
          color: "danger",
          autohide: true,
        });
      }
    },

    async onSubmit() {
      this.validationError = null;
      this.showAlert = false;

      // Validation
      if (!this.form.producer?.role?.id) {
        this.validationError = this.$t("models.mpc.validation.producer");
        this.showAlert = true;
        return;
      }

      if (!this.form.market_wh || this.form.market_wh === 0) {
        this.validationError = this.$t("models.mpc.validation.warehouse");
        this.showAlert = true;
        return;
      }

      if (!this.form.delivery_type || this.form.delivery_type === 0) {
        this.validationError = this.$t("models.mpc.validation.delivery_type");
        this.showAlert = true;
        return;
      }

      if (!this.form.delivery_method || this.form.delivery_method === 0) {
        this.validationError = this.$t("models.mpc.validation.delivery_method");
        this.showAlert = true;
        return;
      }

      const requestParams = this.form;

      // check user permissions before submitting
      const perm = this.$store.state.connections.current.permissions.role_lg;
      if (perm !== 2) {
        this.validationError = this.$t(
          "models.market_plan.addForm.validation.permissions"
        );
        this.showAlert = true;
        return;
      }

      let response;
      if (this.editMode && this.editId) {
        response = await apiPatch(
          "role_marketproducer_connections",
          this.editId,
          requestParams,
          [this.$store.state.role.id]
        );
      } else {
        response = await apiPost(
          "role_marketproducer_connections",
          requestParams,
          [this.$store.state.role.id]
        );
      }

      if (response) {
        this.$emit("submitted");
        this.closeModal();
      }
    },

    closeModal() {
      this.$emit("update:show", false);
      this.validationError = null;
      this.showAlert = false;
    },

    resetForm() {
      this.form = {
        producer: { role: { name: null, id: null } },
        market_wh: 0,
        delivery_type: 0,
        delivery_method: 0,
        order_offset: 0,
        delivery_offset: 0,
      };
      this.producerSearch = "";
      this.validationError = null;
      this.showAlert = false;
    },
  },

  async created() {
    await this.fetchProducers();
  },
};
</script>
